import React, { useEffect, useState } from 'react';
import { FileText, Plus, Printer, Download, RotateCcw, Trash2, Mail } from 'lucide-react';
import CiEmailDialog from './CiEmailDialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

const INCOTERMS = ['EXW', 'FCA', 'FAS', 'FOB', 'CFR', 'CIF', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP'];
const REASONS = ['Sale', 'Purchase', 'Repair / Return', 'Warranty Replacement', 'Sample', 'Gift', 'Temporary Export'];
const NUM = ['packages', 'total_weight', 'freight_charge', 'insurance_charge'];

const F = ({ label, k, form, set, type = 'text', list, cls = '' }) => (
  <div className={cls}><Label className="text-xs">{label}</Label>
    <Input type={type} list={list} value={form[k] ?? ''} onChange={(e) => set(k, e.target.value)} className="h-8 rounded-sm text-sm" data-testid={`ci-${k.replace(/_/g, '-')}`} />
  </div>
);
const T = ({ label, k, form, set }) => (
  <div><Label className="text-xs">{label}</Label><Textarea rows={2} value={form[k] ?? ''} onChange={(e) => set(k, e.target.value)} className="rounded-sm text-sm" data-testid={`ci-${k.replace(/_/g, '-')}`} /></div>
);

const LinesTable = ({ lines, setLines }) => {
  const upd = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const cols = [['description', 'Description', 'w-56'], ['item_sku', 'SKU', 'w-24'], ['hs_code', 'HS', 'w-24'], ['country_of_origin', 'Origin', 'w-16'], ['eccn', 'ECCN', 'w-20'], ['quantity', 'Qty', 'w-16'], ['uom', 'UOM', 'w-14'], ['unit_value', 'Unit value', 'w-24']];
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-sm">
      <table className="text-xs w-full">
        <thead className="bg-slate-50"><tr>{cols.map(([, h]) => <th key={h} className="text-left px-1.5 py-1 font-medium text-slate-500">{h}</th>)}<th /></tr></thead>
        <tbody>{lines.map((l, i) => (
          <tr key={i} data-testid={`ci-line-${i}`}>{cols.map(([k, , w]) => (
            <td key={k} className="p-0.5"><Input type={['quantity', 'unit_value'].includes(k) ? 'number' : 'text'} value={l[k] ?? ''} onChange={(e) => upd(i, k, e.target.value)} className={`h-7 rounded-sm text-xs ${w}`} data-testid={`ci-line-${k.replace(/_/g, '-')}-${i}`} /></td>
          ))}<td><Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => setLines(lines.filter((_, j) => j !== i))} data-testid={`ci-line-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button></td></tr>
        ))}</tbody>
      </table>
      <Button size="sm" variant="ghost" className="m-1 h-7 text-xs" onClick={() => setLines([...lines, { description: '', quantity: 1, unit_value: 0 }])} data-testid="ci-add-line"><Plus className="w-3 h-3 mr-1" /> Add line</Button>
    </div>
  );
};

// Commercial (customs) invoice editor for a PO ('po'), Packing Slip ('ps') or Bill of Lading ('bol').
export const CommercialInvoiceButton = ({ sourceType, sourceId, testId = 'ci-open-btn' }) => {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const base = `/commercial-invoices/${sourceType}/${sourceId}`;

  const load = async (path = base) => {
    try { const r = await api().get(path); setForm(r.data); } catch (e) { toast.error(e.response?.data?.detail || 'Could not load commercial invoice'); }
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    const body = { ...form, lines: form.lines.map(l => ({ ...l, quantity: Number(l.quantity) || 0, unit_value: Number(l.unit_value) || 0 })) };
    NUM.forEach(k => { body[k] = Number(form[k]) || 0; });
    body.packages = Math.round(body.packages);
    const r = await api().put(base, body);
    setForm(r.data);
  };
  const run = async (kind) => {
    setBusy(true);
    try {
      await save();
      if (kind === 'save') { toast.success('Commercial invoice saved'); return; }
      if (kind === 'email') { setEmailOpen(true); return; }
      const r = await api().get(`${base}/${kind}`, { responseType: kind === 'pdf' ? 'blob' : 'text' });
      if (kind === 'pdf') {
        const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
        const a = document.createElement('a'); a.href = url; a.download = `${form.invoice_number}.pdf`; a.click(); URL.revokeObjectURL(url);
      } else { const w = window.open('', '_blank'); if (!w) { toast.error('Pop-up blocked'); return; } w.document.write(r.data); w.document.close(); setTimeout(() => w.print(), 400); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const sub = form ? form.lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_value || 0), 0) : 0;
  const p = { form, set };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="rounded-sm" data-testid={testId}><FileText className="w-3.5 h-3.5 mr-1" /> Commercial Invoice</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="ci-dialog">
          <DialogHeader><DialogTitle>Commercial Invoice {form?.saved === false && <span className="text-xs font-normal text-slate-500 ml-2">(prefilled from {form.source_number}, not saved yet)</span>}</DialogTitle></DialogHeader>
          {!form ? <div className="py-10 text-center text-sm text-slate-500">Loading…</div> : (
            <div className="space-y-4">
              <datalist id="ci-incoterms">{INCOTERMS.map(x => <option key={x} value={x} />)}</datalist>
              <datalist id="ci-reasons">{REASONS.map(x => <option key={x} value={x} />)}</datalist>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                <F label="Invoice #" k="invoice_number" {...p} /><F label="Date" k="invoice_date" type="date" {...p} />
                <F label="Currency" k="currency" {...p} /><F label="Incoterms" k="incoterms" list="ci-incoterms" {...p} />
                <F label="Reason for export" k="reason_for_export" list="ci-reasons" cls="col-span-2" {...p} />
                <F label="Country of export" k="country_of_export" {...p} /><F label="Country of destination" k="country_of_destination" {...p} />
                <F label="Carrier" k="carrier" {...p} /><F label="Tracking / PRO #" k="tracking_number" {...p} />
                <F label="Packages" k="packages" type="number" {...p} />
                <div className="flex gap-1"><F label="Total weight" k="total_weight" type="number" cls="flex-1" {...p} />
                  <div><Label className="text-xs">Unit</Label><select value={form.weight_uom} onChange={(e) => set('weight_uom', e.target.value)} className="h-8 border border-slate-200 rounded-sm px-1 text-sm bg-white" data-testid="ci-weight-uom"><option>lb</option><option>kg</option></select></div>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <div className="space-y-1.5 border border-slate-200 rounded-sm p-2"><div className="text-[10px] uppercase tracking-wider text-slate-500">Exporter / Seller</div>
                  <F label="Name" k="exporter_name" {...p} /><T label="Address" k="exporter_address" {...p} /><F label="Tax / Business #" k="exporter_tax_id" {...p} /></div>
                <div className="space-y-1.5 border border-slate-200 rounded-sm p-2"><div className="text-[10px] uppercase tracking-wider text-slate-500">Importer / Buyer</div>
                  <F label="Name" k="importer_name" {...p} /><T label="Address" k="importer_address" {...p} /><F label="Tax / Business #" k="importer_tax_id" {...p} /><F label="Contact" k="importer_contact" {...p} /></div>
                <div className="space-y-1.5 border border-slate-200 rounded-sm p-2"><div className="text-[10px] uppercase tracking-wider text-slate-500">Ship To</div>
                  <T label="Address" k="ship_to" {...p} /><F label="Signer name" k="signer_name" {...p} /><F label="Signer title" k="signer_title" {...p} /></div>
              </div>
              <LinesTable lines={form.lines} setLines={(lines) => set('lines', lines)} />
              <div className="grid md:grid-cols-3 gap-3 items-start">
                <T label="Notes" k="notes" {...p} /><T label="Declaration" k="declaration" {...p} />
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between"><span>Goods value</span><span className="mono">{sub.toFixed(2)}</span></div>
                  <div className="grid grid-cols-2 gap-2"><F label="Freight" k="freight_charge" type="number" {...p} /><F label="Insurance" k="insurance_charge" type="number" {...p} /></div>
                  <div className="flex justify-between bg-slate-900 text-white px-2 py-1 rounded-sm"><span>Total ({form.currency})</span><span className="mono font-bold" data-testid="ci-total">{(sub + Number(form.freight_charge || 0) + Number(form.insurance_charge || 0)).toFixed(2)}</span></div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-1">
            <Button variant="ghost" onClick={() => { if (window.confirm('Replace everything with fresh values from the source document?')) load(`${base}/defaults`); }} className="rounded-sm mr-auto" data-testid="ci-reset"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset from source</Button>
            <Button variant="outline" onClick={() => run('save')} disabled={busy || !form} className="rounded-sm" data-testid="ci-save">Save</Button>
            <Button variant="outline" onClick={() => run('email')} disabled={busy || !form} className="rounded-sm" data-testid="ci-email"><Mail className="w-3.5 h-3.5 mr-1" /> Save & Email</Button>
            <Button variant="outline" onClick={() => run('pdf')} disabled={busy || !form} className="rounded-sm" data-testid="ci-pdf"><Download className="w-3.5 h-3.5 mr-1" /> Save & PDF</Button>
            <Button onClick={() => run('print')} disabled={busy || !form} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="ci-print"><Printer className="w-3.5 h-3.5 mr-1" /> Save & Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CiEmailDialog api={api} base={base} open={emailOpen} onOpenChange={setEmailOpen} invoiceNumber={form?.invoice_number} />
    </>
  );
};

export default CommercialInvoiceButton;
